import {
  BaseModel,
  Watchable,
  useInScopeModel,
  type ReadonlyEventWatchable,
  type ReadonlyWatchable,
} from '@cotera/client/v0/actions/framework';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { Assert } from '@cotera/utilities';
import { emitChatBotConfigUpdated } from '@cotera/client/app/etc/emit-chat-bot-config-updated';
import type { WatchablesFactory } from '@cotera/client/app/v1/watchables/watchables-factory';

export type TeamsBotSettingsState = {
  loaded: boolean;
  installed: boolean;
  tenantId?: string;
  channels: Array<{
    channelId: string;
    channelName: string;
    agentId: string | null;
  }>;
  channelsError?: string;
};

const EMPTY_TEAMS_BOT_SETTINGS_STATE: TeamsBotSettingsState = {
  loaded: false,
  installed: false,
  channels: [],
};

function teamsBotSettingsSignature(a: TeamsBotSettingsState): string {
  return `${a.loaded}|${a.installed}|${a.tenantId ?? ''}|${
    a.channelsError ?? ''
  }|${a.channels
    .map((channel) => `${channel.channelId}:${channel.agentId ?? ''}`)
    .join(';')}`;
}

type TeamsBotSettingsModelProps = {
  orgId: string;
  client: Pick<TenantedClient, 'toolsV2'>;
  watchablesFactory: Pick<
    WatchablesFactory,
    'makeChatBotConfigUpdatedWatchable'
  >;
};

export class TeamsBotSettingsModel extends BaseModel {
  private readonly orgId: string;
  private readonly client: Pick<TenantedClient, 'toolsV2'>;
  private readonly _configWatchable: ReadonlyEventWatchable<TeamsBotSettingsState>;
  private readonly _status: ReadonlyWatchable<'loading' | 'ready'>;
  private readonly _pendingChannelId = Watchable.fromValue<string | null>(null);

  constructor(props: TeamsBotSettingsModelProps) {
    super('teams-bot-settings');
    this.orgId = props.orgId;
    this.client = props.client;

    this._configWatchable =
      props.watchablesFactory.makeChatBotConfigUpdatedWatchable<TeamsBotSettingsState>(
        {
          runOnMount: true,
          initialValue: EMPTY_TEAMS_BOT_SETTINGS_STATE,
          equalityFn: (left, right) =>
            teamsBotSettingsSignature(left) ===
            teamsBotSettingsSignature(right),
          filter: (payload) =>
            payload?.orgId === this.orgId && payload.bot === 'teams',
          fn: async () => this.fetchState(),
        }
      );

    this._status = Watchable.from((get) => {
      return get(this._configWatchable).loaded ? 'ready' : 'loading';
    });
  }

  get config(): ReadonlyWatchable<TeamsBotSettingsState> {
    return this._configWatchable;
  }

  get status(): ReadonlyWatchable<'loading' | 'ready'> {
    return this._status;
  }

  get pendingChannelId(): ReadonlyWatchable<string | null> {
    return this._pendingChannelId;
  }

  private async fetchState(): Promise<TeamsBotSettingsState> {
    const installation = Assert.assertOk(
      await this.client.toolsV2.getTeamsBotInstallation({})
    );

    if (installation.installed !== true) {
      return {
        loaded: true,
        installed: false,
        channels: [],
      };
    }

    const channelsResult = await this.client.toolsV2.listTeamsChannelAgents({});
    if (channelsResult.isErr()) {
      const channelsError =
        channelsResult.error.t === 'application'
          ? channelsResult.error.err.message
          : 'Failed to load Teams channel agents';
      return {
        loaded: true,
        installed: true,
        tenantId: installation.tenantId,
        channels: [],
        channelsError,
      };
    }

    return {
      loaded: true,
      installed: true,
      tenantId: installation.tenantId,
      channels: channelsResult.value.channels,
    };
  }

  async refresh(): Promise<void> {
    await this._configWatchable.refresh();
  }

  async setChannelAgent(
    channelId: string,
    agentId: string,
    confirmShare = false
  ): Promise<{ needsShare: boolean }> {
    this._pendingChannelId.set(channelId);
    try {
      const result = Assert.assertOk(
        await this.client.toolsV2.setTeamsChannelAgent({
          channelId,
          agentId,
          confirmShare,
        })
      );
      if (result.status === 'needs-share') {
        return { needsShare: true };
      }
      emitChatBotConfigUpdated(this.orgId, 'teams');
      await this._configWatchable.refresh();
      return { needsShare: false };
    } finally {
      this._pendingChannelId.set(null);
    }
  }

  async clearChannelAgent(channelId: string): Promise<void> {
    this._pendingChannelId.set(channelId);
    try {
      Assert.assertOk(
        await this.client.toolsV2.clearTeamsChannelAgent({ channelId })
      );
      emitChatBotConfigUpdated(this.orgId, 'teams');
      await this._configWatchable.refresh();
    } finally {
      this._pendingChannelId.set(null);
    }
  }

  dispose(): void {
    this._configWatchable.unsubscribe();
  }
}

export const useTeamsBotSettingsModel = (): TeamsBotSettingsModel => {
  const model = useInScopeModel(TeamsBotSettingsModel);
  if (model === null) {
    throw new Error('TeamsBotSettingsModel not found in scope');
  }
  return model;
};
